/// <reference lib="dom" />
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { Invoice, Item, User } from 'shared/types.js';
import { formatDate, currencyFormatter } from './utils.js';
import { api } from './api.js';
import { InvoiceDetail } from './common.js';

const MultiSelectFilter = ({ options, value, onChange, placeholder }: { options: string[], value: string, onChange: (value: string) => void, placeholder: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedValues = useMemo(() => new Set(value.split(',').filter(Boolean)), [value]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);

    const handleToggle = (option: string) => {
        const newSelectedValues = new Set(selectedValues);
        if (newSelectedValues.has(option)) {
            newSelectedValues.delete(option);
        } else {
            newSelectedValues.add(option);
        }
        onChange(Array.from(newSelectedValues).join(','));
    };

    const displayValue = value ? value.split(',').join(', ') : '';

    return (
        <div style={{ position: 'relative' }} ref={ref}>
            <input
                type="text"
                readOnly
                value={displayValue}
                onClick={() => setIsOpen(!isOpen)}
                placeholder={placeholder}
                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}
            />
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'var(--panel-bg-color)', border: '1px solid var(--border-color)', zIndex: 10 }}>
                    {options.map(option => (
                        <label key={option} style={{ display: 'block', padding: '0.5rem', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selectedValues.has(option)}
                                onChange={() => handleToggle(option)}
                                style={{ marginRight: '0.5rem' }}
                            />
                            {option}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};



const OperatorFilter = ({ value, onChange, placeholder, type = 'text' }: { value: string, onChange: (value: string) => void, placeholder: string, type?: 'text' | 'date' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [operator, setOperator] = useState('=');
    const [inputValue, setInputValue] = useState('');
    const [inputValue2, setInputValue2] = useState(''); // For between
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);

    useEffect(() => {
        if (!value) {
            setOperator('=');
            setInputValue('');
            setInputValue2('');
            return;
        }
        try {
            const parsed = JSON.parse(value);
            if (parsed.operator) {
                setOperator(parsed.operator);
                if (parsed.operator === 'between') {
                    setInputValue(parsed.value1 || '');
                    setInputValue2(parsed.value2 || '');
                } else {
                    setInputValue(parsed.value || '');
                    setInputValue2('');
                }
            } else {
                setOperator('=');
                setInputValue('');
                setInputValue2('');
            }
        } catch (e) {
            setOperator('=');
            setInputValue('');
            setInputValue2('');
        }
    }, [value]);

    const handleValueChange = (op: string, v1: string, v2: string) => {
        if (op === 'between') {
            onChange(JSON.stringify({ operator: op, value1: v1, value2: v2 }));
        } else {
            onChange(JSON.stringify({ operator: op, value: v1 }));
        }
    };
    
    const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newOperator = e.target.value;
        setOperator(newOperator);
        handleValueChange(newOperator, inputValue, inputValue2);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newInputValue = e.target.value;
        setInputValue(newInputValue);
        handleValueChange(operator, newInputValue, inputValue2);
    };

    const handleInputChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newInputValue = e.target.value;
        setInputValue2(newInputValue);
        handleValueChange(operator, inputValue, newInputValue);
    };

    const displayValue = useMemo(() => {
        if (!value) return '';
        try {
            const parsed = JSON.parse(value);
            if (!parsed.operator) return '';

            if (parsed.operator === 'between') {
                const v1 = parsed.value1 || '';
                const v2 = parsed.value2 || '';
                if (!v1 && !v2) return '';
                return `${v1} and ${v2}`;
            }
            
            const v = parsed.value || '';
            if (!v) return '';

            return `${parsed.operator} ${v}`;
        } catch (e) {
            return '';
        }
    }, [value]);

    return (
        <div style={{ position: 'relative' }} ref={ref}>
            <input
                type="text"
                readOnly
                value={displayValue}
                onClick={() => setIsOpen(!isOpen)}
                placeholder={placeholder}
                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}
            />
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'var(--panel-bg-color)', border: '1px solid var(--border-color)', zIndex: 10, display: 'flex', gap: '0.5rem', padding: '0.5rem', flexDirection: 'column', alignItems: 'center' }}>
                    <select value={operator} onChange={handleOperatorChange} style={{ padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}>
                        <option value="=">=</option>
                        <option value="<">&lt;</option>
                        <option value=">">&gt;</option>
                        <option value="between">Between</option>
                    </select>
                    <input
                        type={type}
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Value"
                        style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}
                    />
                    {operator === 'between' && (
                        <>
                            <span style={{fontSize: '0.8rem'}}>and</span>
                            <input
                                type={type}
                                value={inputValue2}
                                onChange={handleInputChange2}
                                placeholder="Value 2"
                                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const getInitialDateFilter = () => {
    return {};
};

const hasSearchableFilters = (filterValues: Record<string, string>) => {
    return Object.values(filterValues).some(v => {
        const value = v?.trim();
        if (!value) return false;

        try {
            const parsed = JSON.parse(value);
            if (parsed.operator) {
                if (parsed.operator === 'between') {
                    return !!(parsed.value1?.trim() && parsed.value2?.trim());
                }
                return !!parsed.value?.trim();
            }
        } catch (e) {
            // Not a JSON filter from OperatorFilter, so it must be from other filter types.
        }
        return true;
    });
};

const AdvancedSearchView = ({ onBack, canViewCosts, canAccessInventoryLookup, canEditInvoices }: { onBack: () => void, canViewCosts: boolean, canAccessInventoryLookup: boolean, canEditInvoices: boolean }) => {
    const [results, setResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState<Record<string, string>>(getInitialDateFilter());
    const [submittedFilters, setSubmittedFilters] = useState<Record<string, string>>(getInitialDateFilter());
    const [sort, setSort] = useState<{ column: string; direction: 'asc' | 'desc' }>({ column: 'date', direction: 'desc' });
    const [clerks, setClerks] = useState<string[]>([]);
    const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [invoiceItems, setInvoiceItems] = useState<Item[]>([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    const allColumns = useMemo(() => [
        { key: 'type', label: 'Type', width: '105px' },
        { key: 'date', label: 'Date', isDate: true, width: '140px' },
        { key: 'doc_no', label: 'Doc #', width: '100px' },
        { key: 'cust_no', label: 'Cust #', width: '100px' },
        { key: 'job', label: 'Job', width: '70px' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'reference', label: 'Reference' },
        { key: 'po_no', label: 'PO #' },
        { key: 'line_no', label: 'Ln', width: '50px' },
        { key: 'sku', label: 'SKU' },
        { key: 'description', label: 'Description' },
        { key: 'sell_qty', label: 'Qty', isNumeric: true, width: '100px' },
        { key: 'sell_price', label: 'Price', isCurrency: true, width: '100px' },
        ...(canViewCosts ? [{ key: 'cost_stk', label: 'Cost', isCurrency: true, width: '100px' }] : []),
        { key: 'total', label: 'Inv Total', isCurrency: true, width: '120px' },
        { key: 'clerk', label: 'Clerk', width: '90px' },
    ], [canViewCosts]);

    const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => 
        new Set(allColumns.filter(c => !['type', 'job', 'line_no', 'sell_price', 'total', 'clerk'].includes(c.key)).map(c => c.key))
    );

    const columns = useMemo(() => allColumns.filter(c => visibleColumnKeys.has(c.key)), [allColumns, visibleColumnKeys]);

    const toggleColumn = (key: string) => {
        setVisibleColumnKeys(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    useEffect(() => {
        const fetchClerks = async () => {
            try {
                const data = await api.get<string[]>('/api/clerks');
                setClerks(data);
            } catch (error) {
                // no-op
            }
        };
        fetchClerks();
    }, []);

    const hasActiveFilters = useMemo(() => {
        return hasSearchableFilters(submittedFilters);
    }, [submittedFilters]);

    const fetchData = useCallback(async (filterValues: Record<string, string>, sortValue: { column: string; direction: 'asc' | 'desc' }) => {
        if (hasSearchableFilters(filterValues)) {
            setIsLoading(true);
            try {
                const params = new URLSearchParams({
                    filters: JSON.stringify(filterValues),
                    sort: JSON.stringify(sortValue),
                });
                const data = await api.get<{ results: any[] }>(`/api/invoices/advanced-search?${params.toString()}`);
                setResults(data.results);
            } catch (error) {
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        } else {
            setResults([]);
        }
    }, []);

    const handleFilterChange = (column: string, value: string) => {
        setFilters(prev => ({ ...prev, [column]: value }));
    };

    const handleSearch = useCallback(() => {
        setSubmittedFilters(filters);
        void fetchData(filters, sort);
    }, [fetchData, filters, sort]);

    const handleFilterKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    const handleSort = (column: string) => {
        setSort(prev => {
            const nextSort = {
                column,
                direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
            } as const;
            if (hasSearchableFilters(submittedFilters)) {
                void fetchData(submittedFilters, nextSort);
            }
            return nextSort;
        });
    };

    const handleClearFilters = () => {
        setFilters({});
        setSubmittedFilters({});
        setResults([]);
    };

    const handleRowClick = async (invoice: any) => {
        setSelectedInvoice(invoice);
        setIsLoadingItems(true);
        try {
            const fetchedItems = await api.get<Item[]>(`/api/invoices/${invoice.id}/items`);
            setInvoiceItems(fetchedItems);
        } catch (error) {
            setInvoiceItems([]);
        } finally {
            setIsLoadingItems(false);
        }
    };

    const handleSaveChanges = async (updatedInvoice: Invoice) => {
        await api.patch(`/api/document/${updatedInvoice.source}/${updatedInvoice.doc_no}/po-number`, { po_no: updatedInvoice.po_no });
        
        setSelectedInvoice(updatedInvoice);
        setResults(prev => prev.map(inv => (inv.id === updatedInvoice.id) ? {...inv, ...updatedInvoice} : inv));
    };

    const formatCell = (item: any, column: { key: string, isDate?: boolean, isCurrency?: boolean, isNumeric?: boolean }) => {
        const value = item[column.key];
        if (value === null || value === undefined) return '';
        if (column.isDate) return formatDate(value);
        if (column.isCurrency) return Number(value).toFixed(2);
        return String(value);
    };

    return (
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <button onClick={onBack} className="btn-secondary" style={{ alignSelf: 'flex-start' }}>&larr; Back to Simple Search</button>
                <div style={{ position: 'relative', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleClearFilters} className="btn-secondary">Clear Filters</button>
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setIsColumnDropdownOpen(prev => !prev)} className="btn-secondary">Columns</button>
                        {isColumnDropdownOpen && (
                            <div style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'var(--panel-bg-color)', border: '1px solid var(--border-color)', zIndex: 10, padding: '0.5rem' }}>
                                {allColumns.map(col => (
                                    <label key={col.key} style={{ display: 'block' }}>
                                        <input
                                            type="checkbox"
                                            checked={visibleColumnKeys.has(col.key)}
                                            onChange={() => toggleColumn(col.key)}
                                            style={{ marginRight: '0.5rem' }}
                                        />
                                        {col.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={handleSearch} className="btn-primary" disabled={isLoading}>Search</button>
                </div>
            </div>
            <div className="table-container" style={{ flexGrow: 1, overflow: 'auto' }}>
                <table className="related-invoices-table" style={{ width: '100%', minWidth: 0 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                            {columns.map(col => (
                                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer', background: 'var(--panel-bg-color)', width: col.width || 'auto' }}>
                                    {col.label}
                                    {sort.column === col.key && (sort.direction === 'desc' ? ' ▼' : ' ▲')}
                                </th>
                            ))}
                        </tr>
                        <tr onKeyDown={handleFilterKeyDown}>
                            {columns.map(col => (
                                <th key={`${col.key}-filter`} style={{ background: 'var(--panel-bg-color)' }}>
                                    {col.key === 'line_no' ? null : col.key === 'type' ? (
                                        <MultiSelectFilter
                                            options={['Invoice', 'Credit']}
                                            value={filters.type || ''}
                                            onChange={(value) => handleFilterChange('type', value)}
                                            placeholder="Filter"
                                        />
                                    ) : col.key === 'clerk' ? (
                                        <MultiSelectFilter
                                            options={clerks}
                                            value={filters.clerk || ''}
                                            onChange={(value) => handleFilterChange('clerk', value)}
                                            placeholder="Filter"
                                        />
                                    ) : col.key === 'date' ? (
                                        <OperatorFilter
                                            value={filters.date || ''}
                                            onChange={(value) => handleFilterChange('date', value)}
                                            placeholder="Filter"
                                            type="date"
                                        />
                                    ) : ['sell_qty', 'sell_price', 'cost_stk', 'total'].includes(col.key) ? (
                                        <OperatorFilter
                                            value={filters[col.key] || ''}
                                            onChange={(value) => handleFilterChange(col.key, value)}
                                            placeholder="Filter"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            placeholder="Filter"
                                            value={filters[col.key] || ''}
                                            onChange={(e) => handleFilterChange(col.key, e.target.value)}
                                            style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', backgroundColor: 'var(--input-bg-color)', border: '1px solid var(--border-color)' }}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={columns.length} className="no-results">Loading...</td></tr>
                        ) : !hasActiveFilters ? (
                            <tr><td colSpan={columns.length} className="no-results">Please apply at least one filter to begin searching.</td></tr>
                        ) : results.length > 0 ? (
                            results.map((item, index) => (
                                <tr key={index} onClick={() => handleRowClick(item)} style={{ cursor: 'pointer' }}>
                                    {columns.map(col => (
                                        <td key={col.key} className={col.isNumeric || col.isCurrency ? 'text-right' : ''}>
                                            {formatCell(item, col)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={columns.length} className="no-results">No results found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {selectedInvoice && (
                <div className="modal-backdrop" onClick={() => setSelectedInvoice(null)}>
                    <div className="modal modal-invoice-detail" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <strong>Invoice Details</strong>
                            <button onClick={() => setSelectedInvoice(null)} aria-label="Close modal">&times;</button>
                        </div>
                        <div className="modal-body">
                            {isLoadingItems ? (
                                <div className="placeholder"><p>Loading details...</p></div>
                            ) : (
                                <InvoiceDetail
                                    invoice={selectedInvoice}
                                    items={invoiceItems}
                                    onClear={() => setSelectedInvoice(null)}
                                    canViewCosts={canViewCosts}
                                    canAccessInventoryLookup={canAccessInventoryLookup}
                                    canEditInvoices={canEditInvoices}
                                    onSaveChanges={handleSaveChanges}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


const InvoiceListItem = ({ invoice, isActive, onSelect }: { invoice: Invoice, isActive: boolean, onSelect: () => void }) => {
    const badgeText = useMemo(() => {
        if (invoice.source === 'save_orders') return 'SO';
        if (invoice.type === 'Credit') return 'CR';
        return 'INV';
    }, [invoice.source, invoice.type]);

    return (
        <li className={isActive ? 'active' : ''} onClick={onSelect}>
            <div className="invoice-list-item-header">
                <div>
                  <strong>{invoice.doc_no}</strong>
                  <span className={`doc-type-badge ${invoice.source}`}>{badgeText}</span>
                    {invoice.status === 'invoiced' && <span className="doc-status-badge invoiced">Invoiced</span>}
                </div>
                <span>{invoice.customer_name}</span>
            </div>
            <div className="invoice-list-item-footer">
              <span>{formatDate(invoice.date)}</span>
              <span className={Number(invoice.total) < 0 ? 'credit' : 'invoice'}>{currencyFormatter.format(Number(invoice.total))}</span>
            </div>
        </li>
    );
};

const InvoiceListContainer = ({
    invoices,
    totalCount,
    activeInvoice,
    onSelectInvoice,
    searchTerm,
    onSearchChange,
    onSearchSubmit,
    currentSearch,
    onPageChange,
    page,
    limit,
    isLoading,
    onAdvancedSearchClick
}: {
    invoices: Invoice[],
    totalCount: number,
    activeInvoice: Invoice | null,
    onSelectInvoice: (invoice: Invoice | null) => void,
    searchTerm: string,
    onSearchChange: (term: string) => void,
    onSearchSubmit: (e: React.FormEvent) => void,
    currentSearch: string,
    onPageChange: (newPage: number) => void,
    page: number,
    limit: number,
    isLoading: boolean,
    onAdvancedSearchClick?: () => void
}) => {
    const isSearchTermTooShort = currentSearch.trim().length > 0 && currentSearch.trim().length < 4;
    return (
        <div className="invoice-list-container">
            <form className="search-bar" onSubmit={onSearchSubmit}>
                <svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
				>
					<path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
				</svg>
                <input
                    type="search"
                    placeholder="Search Doc # or PO #"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.currentTarget.value)}
                    aria-label="Search invoices"
                />
                <button
                    type="submit"
                    className="search-button"
                    disabled={isLoading}
                    style={{
                        borderRadius: onAdvancedSearchClick ? 0 : '0 var(--border-radius) var(--border-radius) 0',
                        borderRight: onAdvancedSearchClick ? 'none' : undefined,
                    }}
                >
                    Search
                </button>
                {onAdvancedSearchClick && (
                    <button
                        type="button"
                        onClick={onAdvancedSearchClick}
                        className="search-button"
                        title="Advanced Search"
                        style={{ padding: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '44px' }}
                    >
                        <svg 
						    style={{ position: 'static' }}
							fill="none"
							height="24"
							viewBox="0 0 64 64"
							width="24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g clip-rule="evenodd" fill="currentColor" fill-rule="evenodd">
								<path d="m42 2c4.4183 0 8 3.58172 8 8v17.5563c-1.3129-.3291-2.6548-.5121-4-.5492v-5.0071h-11v8.2585c-.9097.628-1.7757 1.3467-2.5852 2.1563-1.1013 1.1013-2.0346 2.307-2.7999 3.5852h-8.6149v10h6.0071c.0371 1.3452.2201 2.6871.5492 4h-17.5563c-4.41828 0-8-3.5817-8-8v-32c0-4.41828 3.58172-8 8-8zm-36 34v6c0 2.2091 1.79086 4 4 4h7v-10zm11-4h-11v-10h11zm14 0h-10v-10h10zm15-14h-40v-8c0-2.20914 1.79086-4 4-4h32c2.2091 0 4 1.79086 4 4z"/>
								<path d="m56.4384 34.5361c-6.0481-6.0481-15.8541-6.0481-21.9023 0-6.0481 6.0482-6.0481 15.8542 0 21.9023 5.569 5.569 14.3238 6.0102 20.3976 1.3238l3.6521 3.652c.781.7811 2.0474.7811 2.8284 0 .7811-.781.7811-2.0474 0-2.8284l-3.652-3.6521c4.6864-6.0738 4.2452-14.8286-1.3238-20.3976zm-19.0739 2.8284c4.4861-4.486 11.7594-4.486 16.2455 0 4.486 4.4861 4.486 11.7594 0 16.2455-4.4861 4.486-11.7594 4.486-16.2455 0-4.486-4.4861-4.486-11.7594 0-16.2455z"/>
							</g>
						</svg>
                    </button>
                )}
            </form>
            {isLoading && <div className="no-results">Loading...</div>}
            {!isLoading && invoices.length > 0 && (
                <ul className="invoice-list">
                    {invoices.map(inv => (
                        <InvoiceListItem
                            key={`${inv.source}-${inv.id}`}
                            invoice={inv}
                            isActive={activeInvoice?.id === inv.id && activeInvoice.source === inv.source}
                            onSelect={() => onSelectInvoice(inv)}
                        />
                    ))}
                </ul>
            )}
            {!isLoading && invoices.length === 0 && currentSearch && !isSearchTermTooShort && (
                <p className="no-results">No results found for "{currentSearch}"</p>
            )}
            {!isLoading && currentSearch && isSearchTermTooShort && (
                <p className="no-results">Please enter at least 4 characters to search.</p>
            )}
            <div className="pagination-controls">
                <span>
                    Showing {invoices.length > 0 ? Math.min((page - 1) * limit + 1, totalCount) : 0} - {Math.min(page * limit, totalCount)} of {totalCount}
                </span>
                <div>
                    <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}>Previous</button>
                    <button onClick={() => onPageChange(page + 1)} disabled={page * limit >= totalCount}>Next</button>
                </div>
            </div>
        </div>
    );
};

export const InvoiceSearch = ({ user, initialInvoiceNo = '' }: { user: User, initialInvoiceNo?: string }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentSearch, setCurrentSearch] = useState('');
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
    const [items, setItems] = useState<Item[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [viewMode, setViewMode] = useState<'simple' | 'advanced'>('simple');

    const canViewCosts = user.permissions?.can_view_costs === 'true';
    const canAccessInventoryLookup = user.permissions?.can_access_inventory_lookup === 'true';
    const canEditInvoices = user.permissions?.can_edit_invoices === 'true';
    const limit = 50;
    const fetchInvoices = useCallback(async (term: string, pageNum: number) => {
        if (term.trim().length < 4) {
            setInvoices([]);
            setTotalCount(0);
            return;
        }
        setIsLoading(true);
        try {
            const data = await api.get<{ invoices: Invoice[], totalCount: number }>(`/api/invoices?search=${encodeURIComponent(term)}&page=${pageNum}&limit=${limit}`);
            setInvoices(data.invoices);
            setTotalCount(data.totalCount);
        } catch (error) {
            // No-op
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (currentSearch) {
            fetchInvoices(currentSearch, page);
        }
    }, [currentSearch, page, fetchInvoices]);
    
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setCurrentSearch(searchTerm);
    };

    const handleSelectInvoice = useCallback(async (invoice: Invoice | null) => {
        setActiveInvoice(invoice);
        if (invoice) {
            if (invoice.source === 'save_orders') {
                setItems([]);
                return;
            }
			setIsLoadingItems(true);
            try {
                const fetchedItems = await api.get<Item[]>(`/api/invoices/${invoice.id}/items`);
                setItems(fetchedItems);
            } catch (error) {
                setItems([]);
            } finally {
                setIsLoadingItems(false);
            }
        } else {
            setItems([]);
        }
    }, []);

    useEffect(() => {
        const invoiceNo = initialInvoiceNo.trim();
        if (!invoiceNo) return;

        let isCancelled = false;
        const loadDirectInvoice = async () => {
            setViewMode('simple');
            setSearchTerm(invoiceNo);
            setCurrentSearch('');
            setInvoices([]);
            setTotalCount(0);
            setIsLoadingItems(true);
            try {
                const document = await api.get<Invoice>(`/api/document/by-doc-no?source=invoices&doc_no=${encodeURIComponent(invoiceNo)}`);
                if (!isCancelled) {
                    setInvoices([document]);
                    setTotalCount(1);
                    await handleSelectInvoice(document);
                }
            } catch (error) {
                if (!isCancelled) {
                    setActiveInvoice(null);
                    setItems([]);
                    setInvoices([]);
                    setTotalCount(0);
                    setCurrentSearch(invoiceNo);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingItems(false);
                }
            }
        };

        loadDirectInvoice();

        return () => {
            isCancelled = true;
        };
    }, [initialInvoiceNo, handleSelectInvoice]);

    const handleSaveChanges = async (updatedInvoice: Invoice) => {
        await api.patch(`/api/document/${updatedInvoice.source}/${updatedInvoice.doc_no}/po-number`, { po_no: updatedInvoice.po_no });
        
        setActiveInvoice(updatedInvoice);
        setInvoices(prev => prev.map(inv => (inv.source === updatedInvoice.source && inv.id === updatedInvoice.id) ? updatedInvoice : inv));
    };
    const handleSelectLinkedDoc = async (source: 'invoices' | 'save_orders', docNo: string) => {
        try {
            const document = await api.get<Invoice>(`/api/document/by-doc-no?source=${source}&doc_no=${docNo}`);
            if (document) {
                handleSelectInvoice(document);
            } else {
                window.alert('Linked document not found.');
            }
        } catch (error) {
            window.alert('Error fetching linked document.');
        }
    };
    
    if (viewMode === 'advanced') {
        return (
            <div className="main-content" style={{ gridTemplateColumns: '1fr', overflow: 'auto' }}>
                <AdvancedSearchView 
                    onBack={() => setViewMode('simple')} 
                    canViewCosts={canViewCosts} 
                    canAccessInventoryLookup={canAccessInventoryLookup}
                    canEditInvoices={canEditInvoices}
                />
            </div>
        );
    }
    
    return (
        <div className={`main-content ${activeInvoice ? 'detail-view-active' : ''}`}>
            <InvoiceListContainer
                invoices={invoices}
                totalCount={totalCount}
                activeInvoice={activeInvoice}
                onSelectInvoice={handleSelectInvoice}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchSubmit={handleSearchSubmit}
                currentSearch={currentSearch}
                onPageChange={setPage}
                page={page}
                limit={limit}
                isLoading={isLoading}
                onAdvancedSearchClick={canEditInvoices ? () => setViewMode('advanced') : undefined}
            />
            <div className="invoice-detail-view">
                {activeInvoice && !isLoadingItems && (
                    <InvoiceDetail 
                        invoice={activeInvoice} 
                        items={items} 
                        onClear={() => handleSelectInvoice(null)} 
                        onSelectLinkedDoc={handleSelectLinkedDoc} 
                        canViewCosts={canViewCosts} 
                        canAccessInventoryLookup={canAccessInventoryLookup}
                        onSaveChanges={handleSaveChanges}
                        canEditInvoices={canEditInvoices}
                    />
                )}
                {!activeInvoice && (
                    <div className="placeholder">
                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path></svg>
                        <p>Search for an invoice to view its details</p>
                    </div>
                )}
                 {isLoadingItems && (
                    <div className="placeholder"><p>Loading details...</p></div>
                )}
            </div>
        </div>
    );
};
